import type { ChordStats, SessionSummary, TransitionStats } from '@/types/training';

/**
 * Guards against records that are not shaped the way this build expects.
 *
 * IndexedDB hands back whatever was written, including data from an older
 * build, a half-finished write, or a user who edited it. A single malformed
 * record must not be able to crash the Progress screen — the numbers here are
 * encouragement, not accounting, so a broken row is dropped and the rest of the
 * history still counts.
 */

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || value === undefined || isFiniteNumber(value);
}

export function isValidSession(value: unknown): value is SessionSummary {
  if (typeof value !== 'object' || value === null) return false;
  const session = value as Partial<SessionSummary>;
  return (
    typeof session.id === 'string' &&
    isFiniteNumber(session.startedAt) &&
    typeof session.mode === 'string' &&
    isFiniteNumber(session.durationSec) &&
    Array.isArray(session.chordIds) &&
    isFiniteNumber(session.attempts) &&
    isFiniteNumber(session.accuracy) &&
    isNullableNumber(session.rhythmScore) &&
    isNullableNumber(session.clarityScore) &&
    isNullableNumber(session.avgTransitionSec) &&
    Array.isArray(session.transitions)
  );
}

export function isValidTransition(value: unknown): value is TransitionStats {
  if (typeof value !== 'object' || value === null) return false;
  const stats = value as Partial<TransitionStats>;
  return (
    typeof stats.key === 'string' &&
    typeof stats.from === 'string' &&
    typeof stats.to === 'string' &&
    isFiniteNumber(stats.attempts) &&
    stats.attempts >= 0 &&
    isFiniteNumber(stats.successes) &&
    isFiniteNumber(stats.successRate) &&
    isNullableNumber(stats.avgSec) &&
    isNullableNumber(stats.bestSec) &&
    typeof stats.byBpm === 'object' &&
    stats.byBpm !== null
  );
}

export function isValidChordStats(value: unknown): value is ChordStats {
  if (typeof value !== 'object' || value === null) return false;
  const stats = value as Partial<ChordStats>;
  return (
    typeof stats.chordId === 'string' &&
    isFiniteNumber(stats.attempts) &&
    isFiniteNumber(stats.correct) &&
    isNullableNumber(stats.avgClarity)
  );
}

/** Keep only the records this build can read, and say so once if any were dropped. */
export function keepValid<T>(records: unknown[], isValid: (value: unknown) => value is T, what: string): T[] {
  const kept = records.filter(isValid);
  if (kept.length !== records.length) {
    console.warn(`[storage] dropped ${records.length - kept.length} unreadable ${what} record(s)`);
  }
  return kept;
}
